<template>
  <div class="stock-in-container">
    <header class="header">
      <button @click="handleBack" class="back-btn" aria-label="返回">
        <i class="icon-back">←</i>
      </button>
      <h1 class="title">物料入库</h1>
      <div class="header-right"></div>
    </header>
    
    <main class="content">
      <!-- 入库表单 - 公共信息 -->
      <div class="form-section">
        <h2>入库基本信息</h2>
        
        <div class="form-row">
          <div class="form-group">
            <label>供应商 <span class="required">*</span></label>
            <div class="search-input-wrapper">
              <input 
                type="text" 
                v-model="supplierKeyword" 
                @input="handleSupplierSearch" 
                @focus="showSupplierDropdown = true" 
                placeholder="请输入供应商名称或编码搜索"
                class="search-input"
              >
              <div v-if="showSupplierDropdown" class="dropdown-menu">
                <div v-if="suppliers.length === 0" class="dropdown-empty">暂无数据</div>
                <div 
                  v-for="supplier in suppliers" 
                  :key="supplier.id"
                  @click="selectSupplier(supplier)"
                  class="dropdown-item"
                >
                  {{ supplier.supplierName }} ({{ supplier.bizDeptName || '无部门' }})
                </div>
              </div>
            </div>
            <div v-if="selectedSupplier" class="selected-item">
              {{ selectedSupplier.supplierName }} ({{ selectedSupplier.bizDeptName || '无部门' }})
              <button @click="clearSupplier" class="clear-btn">×</button>
            </div>
          </div>
          
          <div class="form-group">
            <label>仓库 <span class="required">*</span></label>
            <div class="search-input-wrapper">
              <input 
                type="text" 
                v-model="warehouseKeyword" 
                @input="handleWarehouseSearch" 
                @focus="showWarehouseDropdown = true" 
                placeholder="请输入仓库名称或编码搜索"
                class="search-input"
              >
              <div v-if="showWarehouseDropdown" class="dropdown-menu">
                <div v-if="warehouses.length === 0" class="dropdown-empty">暂无数据</div>
                <div 
                  v-for="warehouse in warehouses" 
                  :key="warehouse.id"
                  @click="selectWarehouse(warehouse)"
                  class="dropdown-item"
                >
                  {{ warehouse.wareName }} ({{ warehouse.wareNo || '无编码' }})
                </div>
              </div>
            </div>
            <div v-if="selectedWarehouse" class="selected-item">
              {{ selectedWarehouse.wareName }} ({{ selectedWarehouse.wareNo || '无编码' }})
              <button @click="clearWarehouse" class="clear-btn">×</button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 物料选择弹窗 -->
      <div v-if="showMaterialSelector" class="material-selector-modal">
        <div class="modal-overlay" @click="closeMaterialSelector"></div>
        <div class="modal-content">
          <div class="fixed-search-bar">
            <input 
              type="text" 
              v-model="materialSearchKeyword" 
              @input="handleMaterialSearchInSelector" 
              placeholder="搜索物料编码或名称..."
              class="modal-search-input"
              ref="searchInput"
            >
          </div>
          
          <div class="modal-header">
            <h3>选择物料</h3>
            <button class="close-btn" @click="closeMaterialSelector">×</button>
          </div>
          
          <div class="modal-body">
            <div v-if="loadingMaterials" class="loading-indicator">加载中...</div>
            
            <div v-if="!loadingMaterials && filteredMaterials.length === 0" class="no-materials">
              没有找到匹配的物料
            </div>
            
            <div class="materials-list" v-else>
              <div 
                v-for="material in filteredMaterials" 
                :key="material.id"
                class="material-list-item"
              >
                <div class="material-details">
                  <div class="material-code">物料编码：{{ material.materialNo }}</div>
                  <div class="material-name">物料名称：{{ material.materialDesc }}</div>
                </div>
                <button 
                  class="add-material-btn"
                  @click="addMaterialFromSelector(material)"
                  :disabled="isMaterialAdded(material.id)"
                >
                  {{ isMaterialAdded(material.id) ? '已添加' : '添加' }}
                </button>
              </div>
            </div>
          </div>
          
          <div class="modal-footer">
            <button class="confirm-btn" @click="closeMaterialSelector">完成</button>
          </div>
        </div>
      </div>

      <!-- 库位选择弹窗 - 新增 -->
      <div v-if="showLocationModal" class="location-selector-modal">
        <div class="modal-overlay" @click="closeLocationModal"></div>
        <div class="modal-content">
          <!-- 库位搜索栏 -->
          <div class="fixed-search-bar">
            <input 
              type="text" 
              v-model="currentLocationKeyword" 
              @input="handleLocationSearchInModal" 
              placeholder="搜索库位名称或编码..."
              class="modal-search-input"
              ref="locationSearchInput"
            >
          </div>
          
          <div class="modal-header">
            <h3>选择库位</h3>
            <button class="close-btn" @click="closeLocationModal">×</button>
          </div>
          
          <div class="modal-body">
            <div v-if="loadingLocations" class="loading-indicator">加载中...</div>
            
            <div v-if="!loadingLocations && filteredLocations.length === 0" class="no-materials">
              没有找到匹配的库位
            </div>
            
            <div class="locations-list" v-else>
              <div 
                v-for="location in filteredLocations" 
                :key="location.id"
                class="location-list-item"
                @click="selectLocationFromModal(location)"
              >
                <div class="location-details">
                  <div class="location-code">库位编码：{{ location.wareNo || '无' }}</div>
                  <div class="location-name">库位名称：{{ location.wareName }}</div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="modal-footer">
            <button class="cancel-btn" @click="closeLocationModal">取消</button>
            <button class="confirm-btn" @click="confirmLocationSelection" :disabled="!selectedLocationInModal">
              确认选择
            </button>
          </div>
        </div>
      </div>
      
      <!-- 物料明细 - 卡片式布局 -->
      <div class="materials-section">
        <div class="section-header">
          <h2>物料明细 <span class="required">*</span></h2>
          <button 
            @click="openMaterialSelector" 
            class="add-btn"
            :disabled="!selectedSupplier || !selectedWarehouse"
          >
            添加物料
          </button>
        </div>
        
        <!-- 物料卡片列表 -->
        <div class="materials-card-list">
          <div v-if="materialItems.length === 0" class="empty-state">
            <p>暂无物料，请添加物料</p>
          </div>

          <!-- 单个物料卡片 -->
          <div 
            v-for="(item, index) in materialItems" 
            :key="item.id"
            class="material-card"
          >
            <div class="material-card-header">
              <span class="material-code-label">物料编码：{{ item.code }}</span>
              <button 
                @click="removeMaterialItem(index)" 
                class="card-remove-btn"
                aria-label="删除该物料"
              >
                ×
              </button>
            </div>

            <div class="material-card-body">
              <!-- 第一行：入库数量 -->
              <div class="card-row first-row">
                <div class="card-item">
                  <label class="item-label required">数量：</label>
                  <input 
                    type="number" 
                    v-model.number="item.quantity"
                    min="1"
                    step="1"
                    class="quantity-input"
                    @change="validateItem(item)"
                    placeholder="至少1件"
                  >
                </div>
              </div>

              <!-- 第二行：库位选择（改为按钮触发弹窗） + 备注 -->
              <div class="card-row second-row">
                <!-- 库位选择 - 改造核心：输入框改为按钮触发弹窗 -->
                <div class="card-item location-selector">
                  <label class="item-label required">库位：</label>
                  <!-- 显示已选库位或触发弹窗的按钮 -->
                  <div class="location-selector-btn-group">
                    <button 
                      type="button" 
                      class="location-select-btn"
                      @click="openLocationModal(item)"
                      :disabled="!selectedWarehouse"
                    >
                      {{ item.selectedLocation ? item.selectedLocation.wareName : '选择库位' }}
                    </button>
                    <!-- 清除已选库位按钮 -->
                    <button 
                      v-if="item.selectedLocation"
                      type="button"
                      class="location-clear-btn"
                      @click="clearItemLocation(item)"
                    >
                      ×
                    </button>
                  </div>
                </div>

                <!-- 备注 -->
                <div class="card-item">
                  <label class="item-label">备注：</label>
                  <input 
                    type="text" 
                    v-model="item.remark" 
                    class="remark-input"
                    placeholder="可选：填写物料备注信息"
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 操作按钮 -->
      <div class="action-buttons">
        <button @click="handleSave" class="save-btn" :disabled="!canSave || loading">
          <span v-if="loading" class="loading-spinner"></span>
          {{ loading ? '保存中...' : '保存入库' }}
        </button>
      </div>
    </main>
    
    <!-- 成功提示 -->
    <div v-if="showSuccess" class="success-toast">
      入库成功！
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import * as stockInApi from '@/api/stockIn'

export default {
  name: 'StockIn',
  setup() {
    const router = useRouter()
    const searchInput = ref(null)
    const locationSearchInput = ref(null) // 库位弹窗搜索框引用
    
    // 状态管理
    const loading = ref(false)
    const loadingMaterials = ref(false)
    const loadingLocations = ref(false) // 库位加载状态
    const showSuccess = ref(false)
    const showMaterialSelector = ref(false)
    const showLocationModal = ref(false) // 库位弹窗显示状态
    
    // 搜索关键词
    const supplierKeyword = ref('')
    const warehouseKeyword = ref('')
    const materialSearchKeyword = ref('')
    const currentLocationKeyword = ref('') // 库位弹窗搜索关键词
    
    // 数据列表
    const suppliers = ref([])
    const warehouses = ref([])
    const allMaterials = ref([])
    const filteredMaterials = ref([])
    const allLocations = ref([]) // 所有库位数据
    const filteredLocations = ref([]) // 筛选后的库位数据
    const materialItems = ref([]) // 物料项数组
    
    // 选中的数据
    const selectedSupplier = ref(null)
    const selectedWarehouse = ref(null)
    const currentMaterialItem = ref(null) // 当前操作的物料项（用于库位选择）
    const selectedLocationInModal = ref(null) // 库位弹窗中选中的库位
    
    // 下拉菜单显示状态（仅保留供应商/仓库）
    const showSupplierDropdown = ref(false)
    const showWarehouseDropdown = ref(false)
    
    // 计算是否可以保存
    const canSave = computed(() => {
      return selectedSupplier.value && 
             selectedWarehouse.value && selectedWarehouse.value.id && 
             materialItems.value.length > 0 &&
             materialItems.value.every(item => 
               item.quantity > 0 && 
               item.selectedLocation && 
               item.selectedLocation.id
             )
    })
    
    // 监听供应商和仓库变化，自动加载物料
    watch([() => selectedSupplier.value?.id, () => selectedWarehouse.value?.id], 
      async (newValues) => {
        const [supplierId, warehouseId] = newValues
        if (supplierId && warehouseId) {
          await loadMaterialsAutomatically();
          if (allMaterials.value.length > 0) {
            showMaterialSelector.value = true;
            nextTick(() => {
              searchInput.value?.focus();
            });
          }
        }
      }
    )
    
    // 监听仓库变化，清空所有物料的库位选择
    watch(() => selectedWarehouse.value?.id, async (newVal, oldVal) => {
      if (newVal && newVal !== oldVal) {
        materialItems.value.forEach(item => {
          item.selectedLocation = null;
        });
        // 清空库位数据
        allLocations.value = [];
        filteredLocations.value = [];
      }
    })
    
    // 返回上一页
    const handleBack = () => {
      router.back()
    }
    
    // 供应商搜索
    const handleSupplierSearch = async () => {
      if (supplierKeyword.value.trim()) {
        try {
          const response = await stockInApi.getSuppliers({
            keyword: supplierKeyword.value.trim(),
            page: 1,
            pageSize: 10
          })
          suppliers.value = response.data.records || []
        } catch (error) {
          console.error('获取供应商列表失败:', error)
          suppliers.value = []
        }
      } else {
        suppliers.value = []
      }
    }
    
    // 选择供应商
    const selectSupplier = (supplier) => {
      if (!supplier || !supplier.id) return;
      selectedSupplier.value = supplier;
      supplierKeyword.value = '';
      showSupplierDropdown.value = false;
    };
    
    // 清除供应商
    const clearSupplier = () => {
      selectedSupplier.value = null;
      allMaterials.value = [];
      filteredMaterials.value = [];
      materialItems.value = [];
    }
    
    // 仓库搜索
    const handleWarehouseSearch = async () => {
      if (warehouseKeyword.value.trim()) {
        try {
          const response = await stockInApi.getWarehouses({
            keyword: warehouseKeyword.value.trim()
          })
          warehouses.value = response.data.records || []
        } catch (error) {
          console.error('获取仓库列表失败:', error)
          warehouses.value = []
        }
      } else {
        warehouses.value = []
      }
    }
    
    // 选择仓库
    const selectWarehouse = (warehouse) => {
      if (!warehouse || !warehouse.id) return;
      selectedWarehouse.value = warehouse;
      warehouses.value = [];
      warehouseKeyword.value = '';
      showWarehouseDropdown.value = false;
      
      // 清空所有物料的库位选择
      materialItems.value.forEach(item => {
        item.selectedLocation = null;
      });
      // 清空库位数据
      allLocations.value = [];
      filteredLocations.value = [];
    };
    
    // 清除仓库
    const clearWarehouse = () => {
      selectedWarehouse.value = null;
      materialItems.value = [];
      allMaterials.value = [];
      filteredMaterials.value = [];
      allLocations.value = [];
      filteredLocations.value = [];
    }
    
    // 加载库位列表（改为弹窗服务）
    const loadLocations = async () => {
      if (!selectedWarehouse.value?.id) return;
      
      loadingLocations.value = true;
      try {
        const params = {
          warehouseId: selectedWarehouse.value.id,
          current: 1,
          size: 100  // 加载更多库位选项
        };
        
        if (currentLocationKeyword.value.trim()) {
          params.keyword = currentLocationKeyword.value.trim();
        }
        
        const response = await stockInApi.getLocations(params);
        allLocations.value = response.data.records || [];
        filteredLocations.value = [...allLocations.value];
      } catch (error) {
        console.error('获取库位列表失败:', error);
        allLocations.value = [];
        filteredLocations.value = [];
      } finally {
        loadingLocations.value = false;
      }
    }
    
    // 库位弹窗中搜索库位
    const handleLocationSearchInModal = () => {
      clearTimeout(window.locationSearchTimer);
      window.locationSearchTimer = setTimeout(async () => {
        await loadLocations();
      }, 300);
    }
    
    // 打开库位选择弹窗
    const openLocationModal = async (item) => {
      if (!selectedWarehouse.value?.id) return;
      
      // 记录当前操作的物料项
      currentMaterialItem.value = item;
      // 初始化搜索关键词
      currentLocationKeyword.value = '';
      // 加载库位数据
      await loadLocations();
      // 打开弹窗
      showLocationModal.value = true;
      // 设置默认选中（如果已有选中库位）
      selectedLocationInModal.value = item.selectedLocation || null;
      
      // 弹窗打开后聚焦搜索框
      nextTick(() => {
        locationSearchInput.value?.focus();
      });
    }
    
    // 关闭库位选择弹窗
    const closeLocationModal = () => {
      showLocationModal.value = false;
      currentMaterialItem.value = null;
      selectedLocationInModal.value = null;
    }
    
    // 在库位弹窗中选择库位
    const selectLocationFromModal = (location) => {
      selectedLocationInModal.value = location;
    }
    
    // 确认选择库位
    const confirmLocationSelection = () => {
      if (currentMaterialItem.value && selectedLocationInModal.value) {
        // 将选中的库位赋值给当前物料项
        currentMaterialItem.value.selectedLocation = selectedLocationInModal.value;
        // 关闭弹窗
        closeLocationModal();
      }
    }
    
    // 清除物料项的库位
    const clearItemLocation = (item) => {
      item.selectedLocation = null;
    }
    
    // 自动加载物料
    const loadMaterialsAutomatically = async () => {
      if (!selectedSupplier.value?.id || !selectedWarehouse.value?.id) return;
      
      try {
        loadingMaterials.value = true;
        const response = await stockInApi.getMaterials({
          supplierId: selectedSupplier.value.id,
          storeId: selectedWarehouse.value.id,
          isBad: 0,
          current: 1,
          size: 100
        });
        
        if (response.data && response.data.records) {
          allMaterials.value = (response.data.records || []).map(material => ({
            ...material,
            materialNo: material.materialNo || '',
            materialDesc: material.materialDesc || '',
            materialModel: material.materialModel || ''
          }));
          filteredMaterials.value = [...allMaterials.value];
        } else {
          console.error('物料接口返回格式不正确', response);
          allMaterials.value = [];
          filteredMaterials.value = [];
        }
      } catch (error) {
        console.error('自动加载物料失败:', error);
        allMaterials.value = [];
        filteredMaterials.value = [];
        alert('加载物料失败: ' + (error.message || '未知错误'));
      } finally {
        loadingMaterials.value = false;
      }
    };
    
    // 检查物料是否已添加
    const isMaterialAdded = (materialId) => {
      return materialItems.value.some(item => item.id === materialId);
    }
    
    // 在选择器中搜索物料
    const handleMaterialSearchInSelector = () => {
      const keyword = (materialSearchKeyword.value || '').trim().toLowerCase();
      
      if (!keyword) {
        filteredMaterials.value = [...allMaterials.value];
        return;
      }
      
      filteredMaterials.value = allMaterials.value.filter(material => {
        const materialNo = (material.materialNo || '').toLowerCase();
        const materialDesc = (material.materialDesc || '').toLowerCase();
        const materialModel = (material.materialModel || '').toLowerCase();
        
        return materialNo.includes(keyword) || 
               materialDesc.includes(keyword) ||
               materialModel.includes(keyword);
      });
    }
    
    // 打开物料选择器
    const openMaterialSelector = async () => {
      if (!selectedSupplier.value?.id || !selectedWarehouse.value?.id) return;
      
      if (allMaterials.value.length === 0) {
        await loadMaterialsAutomatically();
      }
      
      materialSearchKeyword.value = '';
      filteredMaterials.value = [...allMaterials.value];
      showMaterialSelector.value = true;
      
      nextTick(() => {
        searchInput.value?.focus();
      });
    }
    
    // 关闭物料选择器
    const closeMaterialSelector = () => {
      showMaterialSelector.value = false;
    }
    
    // 从选择器添加物料
    const addMaterialFromSelector = (material) => {
      if (!material || !material.id || isMaterialAdded(material.id)) return;
      
      const newItem = {
        id: material.id,
        code: material.materialNo,       // 物料编码
        name: material.materialDesc,     // 物料名称
        spec: material.materialModel,    // 规格型号
        unit: material.packageUnitName || '个',  // 单位
        quantity: 1,                     // 入库数量
        logisticsCode: '',               // 物流编码
        remark: '',                      // 备注
        selectedLocation: null           // 选中的库位（仅保留必要字段）
      };
      
      materialItems.value.push(newItem);
    }
    
    // 验证物料数量
    const validateItem = (item) => {
      if (item.quantity < 1 || !Number.isInteger(item.quantity)) {
        item.quantity = 1;
      }
    }
    
    // 移除物料项
    const removeMaterialItem = (index) => {
      materialItems.value.splice(index, 1);
    }
    
    // 保存入库记录
    const handleSave = async () => {
      if (!canSave.value) return;
      
      loading.value = true;
      try {
        const data = {
          wareInType: "0",
          storeId: selectedWarehouse.value.id,
          supplierId: selectedSupplier.value.id,
          supplierName: selectedSupplier.value.supplierName,
          wareInDetailList: materialItems.value.map(item => ({
            materialId: item.id,
            wareInNum: item.quantity,
            imageRemark: "",
            remark: item.remark,
            wareName: item.selectedLocation.wareName // 库位名称放在明细中
          }))
        };
        
        await stockInApi.saveStockIn(data);
        
        showSuccess.value = true;
        
        // 重置表单
        setTimeout(() => {
          selectedSupplier.value = null;
          selectedWarehouse.value = null;
          materialItems.value = [];
          allMaterials.value = [];
          filteredMaterials.value = [];
          allLocations.value = [];
          filteredLocations.value = [];
          showSuccess.value = false;
        }, 2000);
      } catch (error) {
        console.error('保存入库记录失败:', error);
        alert('保存失败: ' + (error.message || '未知错误'));
      } finally {
        loading.value = false;
      }
    }
    
    // 点击外部关闭下拉菜单（仅供应商/仓库）
    onMounted(() => {
      const handleClickOutside = (event) => {
        if (!event.target.closest('.search-input-wrapper')) {
          showSupplierDropdown.value = false;
          showWarehouseDropdown.value = false;
        }
      };
      
      document.addEventListener('click', handleClickOutside);
      
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    });
    
    return {
      searchInput,
      locationSearchInput,
      loading,
      loadingMaterials,
      loadingLocations,
      showSuccess,
      showMaterialSelector,
      showLocationModal,
      
      supplierKeyword,
      warehouseKeyword,
      materialSearchKeyword,
      currentLocationKeyword,
      
      showSupplierDropdown,
      showWarehouseDropdown,
      
      selectedSupplier,
      selectedWarehouse,
      currentMaterialItem,
      selectedLocationInModal,
      
      suppliers,
      warehouses,
      allMaterials,
      filteredMaterials,
      allLocations,
      filteredLocations,
      materialItems,
      
      canSave,
      
      handleBack,
      handleSupplierSearch,
      selectSupplier,
      clearSupplier,
      handleWarehouseSearch,
      selectWarehouse,
      clearWarehouse,
      loadLocations,
      handleLocationSearchInModal,
      openLocationModal,
      closeLocationModal,
      selectLocationFromModal,
      confirmLocationSelection,
      clearItemLocation,
      handleMaterialSearchInSelector,
      openMaterialSelector,
      closeMaterialSelector,
      isMaterialAdded,
      addMaterialFromSelector,
      validateItem,
      removeMaterialItem,
      handleSave
    };
  }
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.required {
  color: #ff4d4f;
}

.stock-in-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 20px;
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
}

.back-btn {
  padding: 8px 12px;
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.3s;
}

.back-btn:hover {
  background-color: rgba(255, 255, 255, 0.3);
}

.title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: var(--header-text);
}

.content {
  flex: 1;
  padding: 20px;
  background-color: var(--background-color);
  overflow-y: auto;
}

.form-section,
.materials-section {
  background-color: var(--card-background);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px var(--shadow-light);
}

.form-section h2,
.materials-section h2 {
  font-size: 16px;
  color: var(--text-primary);
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-color);
}

.form-row {
  display: flex;
  gap: 20px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.form-group {
  flex: 1;
  min-width: 250px;
}

.form-group label {
  display: block;
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 8px;
}

.search-input-wrapper {
  position: relative;
  width: 100%;
}

.search-input {
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-color);
}

.search-input:disabled {
  background-color: var(--background-color);
  cursor: not-allowed;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 200px;
  overflow-y: auto;
  background-color: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  box-shadow: 0 2px 8px var(--shadow-light);
  z-index: 9999;
  margin-top: 4px;
  box-sizing: border-box;
}

.dropdown-item {
  padding: 12px;
  cursor: pointer;
  font-size: 14px;
  color: var(--text-primary);
  transition: background-color 0.2s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-item:hover {
  background-color: var(--background-color);
}

.dropdown-empty, .dropdown-hint, .dropdown-loading {
  padding: 12px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 14px;
}

.dropdown-loading {
  color: var(--text-primary);
}

.selected-item {
  margin-top: 8px;
  padding: 8px 12px;
  background-color: rgba(9, 109, 217, 0.1);
  border: 1px solid rgba(186, 231, 255, 0.5);
  border-radius: 4px;
  font-size: 14px;
  color: #096dd9;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.clear-btn {
  background: none;
  border: none;
  font-size: 16px;
  color: var(--text-secondary);
  cursor: pointer;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.clear-btn:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.add-btn {
  padding: 6px 12px;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.add-btn:hover:not(:disabled) {
  background-color: rgba(66, 185, 131, 0.8);
}

.add-btn:disabled {
  background-color: rgba(66, 185, 131, 0.4);
  cursor: not-allowed;
}

/* 卡片式布局样式 */
.materials-card-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.material-card {
  border: 1px solid var(--border-color);
  border-radius: 8px;
  overflow: hidden;
  transition: box-shadow 0.2s;
}

.material-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.material-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background-color: var(--background-color);
  border-bottom: 1px solid var(--border-color);
}

.material-code-label {
  font-size: 14px;
  font-weight: 500;
  color: red;
}

.card-remove-btn {
  background: none;
  border: none;
  font-size: 18px;
  color: var(--text-secondary);
  cursor: pointer;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.card-remove-btn:hover {
  background-color: rgba(255, 77, 79, 0.1);
  color: #ff4d4f;
}

.material-card-body {
  padding: 16px;
}

.card-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 12px;
}

.card-row.second-row {
  margin-bottom: 0;
}

.card-item {
  flex: 1;
  min-width: 250px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.item-label {
  font-size: 14px;
  color: var(--text-secondary);
  white-space: nowrap;
}

.remark-input {
  flex: 1;
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.remark-input:focus {
  outline: none;
  border-color: var(--primary-color);
}

.quantity-input {
  width: 100px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 14px;
}

.quantity-input:focus {
  outline: none;
  border-color: var(--primary-color);
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: var(--text-secondary);
  background-color: var(--background-color);
  border-radius: 8px;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
}

.save-btn {
  padding: 10px 20px;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.save-btn:hover:not(:disabled) {
  background-color: rgba(66, 185, 131, 0.8);
}

.save-btn:disabled {
  background-color: rgba(66, 185, 131, 0.4);
  cursor: not-allowed;
}

.loading-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.success-toast {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 12px 20px;
  background-color: #52c41a;
  color: white;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 2000;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* 弹窗通用样式 */
.modal-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-content {
  position: relative;
  width: 90%;
  max-width: 800px;
  max-height: 80vh;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
}

.fixed-search-bar {
  position: sticky;
  top: 0;
  padding: 16px 20px;
  background-color: white;
  border-bottom: 1px solid #eee;
  z-index: 10;
}

.modal-header {
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-header h3 {
  font-size: 16px;
  color: #333;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  color: #666;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.close-btn:hover {
  background-color: #f5f5f5;
}

.modal-body {
  padding: 20px;
  flex: 1;
  overflow-y: auto;
}

.modal-search-input {
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.loading-indicator {
  text-align: center;
  padding: 40px;
  color: #666;
}

.no-materials {
  text-align: center;
  padding: 40px;
  color: #999;
}

.modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.confirm-btn {
  padding: 8px 16px;
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.confirm-btn:hover:not(:disabled) {
  background-color: #3aa876;
}

.confirm-btn:disabled {
  background-color: #a0d4ae;
  cursor: not-allowed;
}

.cancel-btn {
  padding: 8px 16px;
  background-color: #fff;
  color: #666;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.cancel-btn:hover {
  background-color: #f5f5f5;
}

/* 物料选择弹窗样式 */
.material-selector-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.materials-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.material-list-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border: 1px solid #eee;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.material-list-item:hover {
  background-color: #f9f9f9;
}

.material-details {
  flex: 1;
}

.material-code {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.material-name {
  font-size: 13px;
  color: #666;
}

.add-material-btn {
  padding: 6px 12px;
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.add-material-btn:disabled {
  background-color: #a0d4ae;
  cursor: not-allowed;
}

.add-material-btn:hover:not(:disabled) {
  background-color: #3aa876;
}

/* 库位选择弹窗样式 - 新增 */
.location-selector-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.locations-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.location-list-item {
  padding: 12px;
  border: 1px solid #eee;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.location-list-item:hover {
  background-color: #f9f9f9;
  border-color: #42b983;
}

/* 选中状态 */
.location-list-item:active,
.location-list-item:focus-within {
  background-color: #f0f9ff;
  border-color: #42b983;
}

.location-details {
  flex: 1;
}

.location-code {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.location-name {
  font-size: 13px;
  color: #666;
}

/* 库位选择按钮组 - 新增 */
.location-selector-btn-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.location-select-btn {
  flex: 1;
  height: 40px;
  padding: 0 12px;
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.2s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.location-select-btn:hover:not(:disabled) {
  border-color: #42b983;
}

.location-select-btn:disabled {
  background-color: #f9f9f9;
  color: #999;
  cursor: not-allowed;
}

.location-clear-btn {
  width: 28px;
  height: 28px;
  background: none;
  border: 1px solid #ddd;
  border-radius: 50%;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.location-clear-btn:hover {
  background-color: #ffebe9;
  border-color: #ff4d4f;
  color: #ff4d4f;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .content {
    padding: 12px;
  }
  
  .form-section,
  .materials-section {
    padding: 16px;
  }
  
  .form-row {
    flex-direction: column;
    gap: 16px;
  }
  
  .form-group, .card-item {
    min-width: auto;
  }
  
  .modal-content {
    width: 95%;
    max-height: 90vh;
  }
}
</style>






